import type { CreateRunOptions, SubmitOptionsFlat } from "../api/types"

/**
 * Submit フォームの **flat** options を、backend `config_loader` が解釈する
 * **canonical config** 構造に変換する。
 *
 * Backend の `_RERUN_INHERITABLE_KEYS` whitelist (runs.py) と整合させる:
 *   - focus.preset / focus.custom_prompt
 *   - proposers.transcript.{model, cost_budget_usd}
 *   - **scorers.llm.{model, cost_budget_usd}** ← Phase 4-followup でこちらにも同時に流す
 *     (UI label「スコア判定モデル」は LlmScorer 側の意味、ただし Phase 1 から
 *      transcript proposer と共通モデルで運用してきたため両者に流す)
 *   - refinement.{top_n, min_duration_sec, max_duration_sec}
 *   - scorers.primary
 *   - media.transcript.language_priority (subtitles_lang → カンマ区切り → list)
 *
 * **Why:** Submit フォームと Re-run (`from_run_id`) で options 構造が違うと
 * 「保存しているように見えて反映されない」事故が起きる。1 箇所に変換層を
 * 閉じ込めて、SPEC §4.7.2 / §4.7.10 に整合させる。
 */
export function toCanonicalOptions(flat: SubmitOptionsFlat): CreateRunOptions {
  const out: Record<string, Record<string, unknown>> = {}
  const focus: Record<string, unknown> = {}
  const proposers_transcript: Record<string, unknown> = {}
  const scorers_llm: Record<string, unknown> = {}
  const refinement: Record<string, unknown> = {}
  const scorers: Record<string, unknown> = {}
  const media_transcript: Record<string, unknown> = {}

  if (flat.focus !== undefined) focus.preset = flat.focus
  if (flat.custom_prompt !== undefined) focus.custom_prompt = flat.custom_prompt
  // Phase 4-followup レビュー対応 (MUST 3/4): Submit UI の「スコア判定モデル」「コスト上限」
  // は LlmScorer (= scorers.llm) と Transcript Proposer の両方に同じ値を流す。
  // UI ラベル「スコア判定モデル」は LlmScorer 側の意味だが、Phase 1 から transcript proposer
  // と共通モデルで運用してきたため、両者に流して整合を取る。
  if (flat.llm_model !== undefined) {
    proposers_transcript.model = flat.llm_model
    scorers_llm.model = flat.llm_model
  }
  if (flat.cost_budget_usd !== undefined) {
    proposers_transcript.cost_budget_usd = flat.cost_budget_usd
    scorers_llm.cost_budget_usd = flat.cost_budget_usd
  }
  if (flat.top_n !== undefined) refinement.top_n = flat.top_n
  if (flat.min_duration_sec !== undefined) refinement.min_duration_sec = flat.min_duration_sec
  if (flat.max_duration_sec !== undefined) refinement.max_duration_sec = flat.max_duration_sec
  if (flat.primary_scorer !== undefined) scorers.primary = flat.primary_scorer
  if (flat.subtitles_lang && flat.subtitles_lang.trim() !== "auto") {
    // "auto" は「指定なし = backend の default (["ja", "en"]) を使う」を意味
    // するセンチネル値。これを `language_priority=["auto"]` として送ると backend は
    // 実言語コードとして "auto" を探しに行き、見つからず fallback で yt-dlp の
    // dict 順序依存の任意言語を pick する (壊れた placeholder entry を引く事故あり)。
    // 明示的言語 ("ja" / "ja,en" / "en" 等) のときだけ送る。
    const langs = flat.subtitles_lang
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
    if (langs.length > 0) {
      media_transcript.language_priority = langs
    }
  }

  if (Object.keys(focus).length > 0) out.focus = focus
  if (Object.keys(proposers_transcript).length > 0) {
    out.proposers = { transcript: proposers_transcript }
  }
  if (Object.keys(refinement).length > 0) out.refinement = refinement
  if (Object.keys(scorers_llm).length > 0) {
    scorers.llm = scorers_llm
  }
  if (Object.keys(scorers).length > 0) out.scorers = scorers
  if (Object.keys(media_transcript).length > 0) {
    out.media = { transcript: media_transcript }
  }
  return out
}
